// src/hooks/useStackedView.js
import { useState, useCallback, useRef } from 'react';
import {
  fetchStackedJobsFromApi,
  fetchStackedJobsWithSearch,
  fetchTasksForJobApi,
  fetchNotesForTaskApi,
} from '../service/api';
import toast from 'react-hot-toast';

// Helper: sort jobs by latest note timestamp (newest first)
const sortJobsByLatestNote = (jobs) => {
  if (!jobs || !jobs.length) return jobs;
  return [...jobs].sort((a, b) => {
    const timeA = a.latestNoteTimeStamp
      ? new Date(a.latestNoteTimeStamp).getTime()
      : a.latestTimeStamp
      ? new Date(a.latestTimeStamp).getTime()
      : 0;
    const timeB = b.latestNoteTimeStamp
      ? new Date(b.latestNoteTimeStamp).getTime()
      : b.latestTimeStamp
      ? new Date(b.latestTimeStamp).getTime()
      : 0;
    return timeB - timeA; // newest first
  });
};

export const useStackedView = (userId, apiUrl, viewMode, activeSearchTerm, hasActiveFilters) => {
  // State
  const [stackedJobs, setStackedJobs] = useState([]);
  const [filteredStackedJobs, setFilteredStackedJobs] = useState([]);
  const [loadingStackedJobs, setLoadingStackedJobs] = useState(false);
  const [expandedStacks, setExpandedStacks] = useState({});
  const [expandedCardLimit, setExpandedCardLimit] = useState({});
  const [notesByTask, setNotesByTask] = useState({});
  const [loadingNotesByTask, setLoadingNotesByTask] = useState({});
  const [tasksByJob, setTasksByJob] = useState({});
  const [loadingTasksByJob, setLoadingTasksByJob] = useState({});
  const [taskPagination, setTaskPagination] = useState({});
  const [highlightedTaskId, setHighlightedTaskId] = useState(null);

  // Guard against duplicate in-flight requests (e.g. StrictMode double-invoked
  // mount effects) so the stacked-jobs endpoint isn't called twice concurrently.
  const stackedJobsInFlightRef = useRef(false);

  // Helper: group notes by job (used in fetchNotesWithFilters)
  const groupFilteredNotesByJob = useCallback((notes) => {
    if (!notes?.length) return [];
    const grouped = {};
    notes.forEach(note => {
      const jobName = note.job || "Unassigned";
      const jobId = note.jobId || note.job;
      if (!grouped[jobName]) {
        grouped[jobName] = {
          jobId, jobName,
          projectId: note.projectId || note.project || "",
          projectName: note.projectName || note.project || "",
          jobDescription: note.jobDescription || note.description || "",
          noteCount: 1,
          notes: [note],
          hasLoadedNotes: true,
          isLoadingNotes: false,
          errorLoadingNotes: null,
          latestTimeStamp: note.timeStamp || note.date,
          lastSiteNoteUserName: note.userName || note.UserName,
          lastSiteNote: note.note || "",
          //friendlyId: note.friendlyId || note.jobFriendlyId || note.jobId || "",
          jobFriendlyId: note.jobFriendlyId  || "",
          workspaceId: note.workspaceId || note.workspace || "",
          workspaceName: note.workspaceName || note.workspace || "",
        };
      } else {
        grouped[jobName].notes.push(note);
        grouped[jobName].noteCount++;
        const noteTime = new Date(note.timeStamp || note.date).getTime();
        const currentLatest = new Date(grouped[jobName].latestTimeStamp || 0).getTime();
        if (noteTime > currentLatest) {
          grouped[jobName].latestTimeStamp = note.timeStamp || note.date;
          grouped[jobName].lastSiteNote = note.note || "";
          grouped[jobName].lastSiteNoteUserName = note.userName || note.UserName;
        }
      }
    });
    const result = Object.values(grouped).sort((a,b) => new Date(b.latestTimeStamp || 0) - new Date(a.latestTimeStamp || 0));
    return result;
  }, []);

  // Fetch stacked jobs (main list or search results)
  const fetchStackedJobs = useCallback(async () => {
    if (!userId) return;
    if (stackedJobsInFlightRef.current) return;
    stackedJobsInFlightRef.current = true;
    setLoadingStackedJobs(true);
    try {
      let jobs;
      if (viewMode === 'stacked' && activeSearchTerm?.trim()) {
        jobs = await fetchStackedJobsWithSearch(userId, activeSearchTerm, apiUrl);
      } else {
        jobs = await fetchStackedJobsFromApi(userId, apiUrl);
      }
      const filtered = jobs.filter(j => j.noteCount > 0);
      const sorted = sortJobsByLatestNote(filtered);
      setStackedJobs(sorted);
    } catch (error) {
      console.error('Error in fetchStackedJobs:', error);
      toast.error('Failed to load stacked jobs');
      setStackedJobs([]);
    } finally {
      setLoadingStackedJobs(false);
      stackedJobsInFlightRef.current = false;
    }
  }, [apiUrl, userId, activeSearchTerm, viewMode]);

  // Fetch tasks for a job
  const fetchTasksForJob = useCallback(async (jobId) => {
    if (!jobId || loadingTasksByJob[jobId]) return;
    setLoadingTasksByJob(prev => ({ ...prev, [jobId]: true }));
    try {
      const tasks = await fetchTasksForJobApi(jobId, apiUrl);
      setTasksByJob(prev => ({ ...prev, [jobId]: tasks }));
      return tasks;
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoadingTasksByJob(prev => ({ ...prev, [jobId]: false }));
    }
  }, [apiUrl, loadingTasksByJob]);

  const updateTaskPriorityLocally = useCallback((taskId, newPriority) => {
  setTasksByJob(prev => {
    const newState = { ...prev };
    for (const jobId in newState) {
      const taskIndex = newState[jobId]?.findIndex(t => t.id === taskId);
      if (taskIndex !== -1) {
        // Create new array for this job to trigger re-render
        newState[jobId] = [...newState[jobId]];
        newState[jobId][taskIndex] = {
          ...newState[jobId][taskIndex],
          priority: newPriority,
        };
        break;
      }
    }
    return newState;
  });
}, []);

  // Fetch notes for a specific task (with pagination)
  const fetchNotesForTask = useCallback(async (taskId, jobId, page = 1, append = false) => {
    if (!taskId || !userId) return;
    if (loadingNotesByTask[taskId] && !append) return;
    setLoadingNotesByTask(prev => ({ ...prev, [taskId]: true }));
    try {
      const pageSize = 20;
      const { notes: formattedNotes, totalCount, hasMore } = await fetchNotesForTaskApi(
        userId, taskId, page, pageSize, apiUrl
      );
      setTaskPagination(prev => ({
        ...prev,
        [taskId]: {
          page,
          hasMore,
          total: totalCount,
          loadedCount: (prev[taskId]?.loadedCount || 0) + formattedNotes.length,
          pageSize,
        }
      }));
      setNotesByTask(prev => ({
        ...prev,
        [taskId]: append ? [...(prev[taskId] || []), ...formattedNotes] : formattedNotes,
      }));
      return formattedNotes;
    } catch (error) {
      console.error('Error fetching task notes:', error);
      toast.error('Failed to load task notes');
    } finally {
      setLoadingNotesByTask(prev => ({ ...prev, [taskId]: false }));
    }
  }, [apiUrl, userId, loadingNotesByTask]);

  // Load more notes for a task (pagination)
  const loadMoreTaskNotes = useCallback(async (taskId, jobId) => {
    const pagination = taskPagination[taskId];
    if (!pagination?.hasMore || loadingNotesByTask[taskId]) return false;
    if (pagination.loadedCount >= pagination.total) {
      setTaskPagination(prev => ({ ...prev, [taskId]: { ...prev[taskId], hasMore: false } }));
      return false;
    }
    const nextPage = pagination.page + 1;
    await fetchNotesForTask(taskId, jobId, nextPage, true);
    return true;
  }, [taskPagination, loadingNotesByTask, fetchNotesForTask]);

  // Toggle expansion of a job stack (wrapper that uses the current expandedStacks from component)
  const toggleStackExpansion = useCallback((jobName, jobId, currentExpandedStacks, currentStackedJobs, currentFilteredStackedJobs) => {
    const isExpanding = !currentExpandedStacks[jobName];
    if (!isExpanding) {
      setExpandedStacks(prev => ({ ...prev, [jobName]: false }));
      return;
    }
    const jobsSource = hasActiveFilters ? currentFilteredStackedJobs : currentStackedJobs;
    const job = jobsSource.find(j => j.jobId === jobId);
    if (!job) return;
    if (job.hasLoadedNotes && job.notes) {
      setExpandedStacks(prev => ({ ...prev, [jobName]: true }));
      return;
    }
    const setJobs = hasActiveFilters ? setFilteredStackedJobs : setStackedJobs;
    setJobs(prev => prev.map(j => j.jobId === jobId ? { ...j, isLoadingNotes: true } : j));
    try {
      fetchTasksForJob(jobId).then(() => {
        const updatedJob = { ...job, isLoadingNotes: false, hasLoadedNotes: true };
        setJobs(prev => prev.map(j => j.jobId === jobId ? updatedJob : j));
        setExpandedStacks(prev => ({ ...prev, [jobName]: true }));
      });
    } catch (error) {
      console.error("Error expanding stack:", error);
      toast.error(`Failed to load data for ${jobName}`);
    }
  }, [hasActiveFilters, fetchTasksForJob]);

  // Expand a card limit (for "show more")
  const loadMoreCards = useCallback((jobName) => {
    setExpandedCardLimit(prev => ({ ...prev, [jobName]: (prev[jobName] || 50) + 10 }));
  }, []);

  // Clear highlighted task
  const clearHighlightedTask = useCallback(() => setHighlightedTaskId(null), []);

  // Refresh stacked view after note add/update/delete
  const refreshStackedViewWithNewNote = useCallback((
    noteData,
    operation,
    fetchNotesWithFilters,
    selectedFilters
  ) => {
    if (!noteData || viewMode !== "stacked") return;

    if (hasActiveFilters) {
      fetchNotesWithFilters(selectedFilters);
    } else {
      const jobId = noteData.jobId;
      setStackedJobs(prev => {
        const jobIndex = prev.findIndex(j => j.jobId?.toString() === jobId?.toString());
        if (jobIndex === -1) return prev;
        const job = { ...prev[jobIndex] };
        if (operation === "add") {
          job.noteCount += 1;
          // Always update preview (no timestamp condition)
          job.latestTimeStamp = noteData.timeStamp || noteData.date;
          job.lastSiteNote = noteData.note || "";
          job.lastSiteNoteUserName = noteData.userName || noteData.UserName;
          if (expandedStacks[job.jobName] && job.hasLoadedNotes) {
            job.notes = [noteData, ...(job.notes || [])];
          }
        } else if (operation === "update") {
          if (job.lastSiteNoteUserName === noteData.userName) {
            job.lastSiteNote = noteData.note || "";
          }
          if (expandedStacks[job.jobName] && job.hasLoadedNotes && job.notes) {
            const noteIndex = job.notes.findIndex(n => n.id === noteData.id);
            if (noteIndex !== -1) job.notes[noteIndex] = noteData;
          }
        }
        const updated = [...prev];
        updated[jobIndex] = job;
        // Re‑sort the entire array after modification
        return sortJobsByLatestNote(updated);
      });
    }
  }, [viewMode, hasActiveFilters, expandedStacks]);

  return {
    // State
    stackedJobs,
    setStackedJobs,
    filteredStackedJobs,
    setFilteredStackedJobs,
    loadingStackedJobs,
    expandedStacks,
    setExpandedStacks,
    expandedCardLimit,
    notesByTask,
    loadingNotesByTask,
    tasksByJob,
    loadingTasksByJob,
    taskPagination,
    highlightedTaskId,
    setHighlightedTaskId,

    // Functions
    groupFilteredNotesByJob,
    fetchStackedJobs,
    fetchTasksForJob,
    updateTaskPriorityLocally,
    fetchNotesForTask,
    loadMoreTaskNotes,
    toggleStackExpansion,
    loadMoreCards,
    clearHighlightedTask,
    refreshStackedViewWithNewNote,
  };
};